import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { DeleteForever } from '@material-ui/icons';

import {
    ShowIf,
    FormValidationMetadata,
    QuestionValidateType,
    confirmModal,
    BackButtonWithStore,
    SingleItemInfoRow,
    loginAlertModal,
    alertModal,
} from '@apply/ui';

import * as Store from '@apply/store';
import { Header } from '../Header';
import { useAuthentication } from '../LoginPage';
import { FormsStore } from '@apply/store';

import BuildRent from './sections/BuildRent';
import Construction from './sections/Construction';
import ConstructionLoc from './sections/ConstructionLoc';
import FixFlip from './sections/FixFlip';
import Land from './sections/Land';
import Model from './sections/Model';
import Rental from './sections/Rental';
import { LoanType } from '@apply/constants';
import { HomePageLink } from '../HomePageLink';

const formConstant = Store.FormsStore.Types.formConstant;

export interface Actions {
    onAddProperty: () => void;
    onRemoveProperty: (propertyGuid: string) => void;
    onChangeBudget: (guid: string, value: Store.PropertiesStore.Types.Attachment) => void;
    onChangeLandBudget: (guid: string, value: Store.PropertiesStore.Types.Attachment) => void;
    onChangeFixFlipBudget: (guid: string, value: Store.PropertiesStore.Types.Attachment) => void;
    onChangePlans: (guid: string, value: Store.PropertiesStore.Types.Attachment) => void;
    onChangeSpecSheet: (guid: string, value: Store.PropertiesStore.Types.Attachment) => void;
    onChangeSurvey: (guid: string, value: Store.PropertiesStore.Types.Attachment) => void;
    onChangePresold: (guid: string, value: boolean) => void;
    onChangeContactIsPrimary: (guid: string, value: Record<string, unknown>) => void;
    onChangeConstructionStarted: (guid: string, value: boolean) => void;
    onChangeIsAquisitionFinancing: (guid: string, value: boolean) => void;
    onChangeRentalIsAquisitionFinancing: (guid: string, value: boolean) => void;
    onChangeAnyExistingDebt: (guid: string, value: boolean) => void;
    onChangePresaleContract: (guid: string, value: Store.PropertiesStore.Types.Attachment) => void;
    onChangeRentalUnit: (guid: string, value: Store.PropertiesStore.Types.RentalUnit) => void;
    onChangeProvideRentalUnitsLater: (guid: string, value: boolean) => void;
    onChangeCurrentPropertyId: (id: string) => void;
    loadStateFromApi?: (apiId: string, setCurrentPropertyId?: string, token?: string) => Promise<boolean | undefined>;
    saveStateToApi?: (apiId: string, complete: false, token?: string) => Promise<Store.BorrowerStore.Types.IdAndToken>;
    loadSavedApplyForms?: (apiId: string, loanType: LoanType) => Promise<FormsStore.Types.FormState[] | undefined>;
    uploadAttachment?: (
        apiId: string,
        loanType: LoanType,
        propertyGuid: string,
        fileType: Store.PropertiesStore.Types.AttachmentType,
        file: File,
        updater: Store.PropertiesStore.Actions.AttachmentUpdater,
        token: string
    ) => Promise<string>;
    onUpdateGeneralContractorWillWork: (guid: string, value: boolean) => void;
    onUpdateGeneralContractorResume: (guid: string, value: Store.PropertiesStore.Types.Attachment) => void;
}

export interface PropertyPageProps {
    properties?: Store.PropertiesStore.Types.PropertiesState;
    currentPropertyId?: string;
    loanType?: LoanType;
    contact?: Store.BorrowerStore.Types.PrincipalInfo;
    actions?: Actions;
    dispatch?: Store.ApplyDispatchType;
}

export const PropertyPage: React.FC<PropertyPageProps> = ({ properties, currentPropertyId, actions, loanType, contact }) => {
    const navigate = useNavigate();
    const thisLocation = useLocation();

    const { apiId } = useParams<{ apiId: string }>();
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const propertyId = params.get('pid');
    const token = params.get('token');
    const adding = !!params.get('adding');

    const { logout } = useAuthentication();
    const [validation, setValidation] = useState<FormValidationMetadata>({});
    const [submitted, setSubmitted] = useState(false);

    const [loading, setLoading] = useState<boolean>(true);
    const [loadError, setLoadError] = useState<string>('');
    const [complete, setComplete] = useState<boolean>(false);
    const [showDelete, setShowDelete] = useState<boolean>(false);

    const [sharedForms, setSharedForms] = useState<FormsStore.Types.FormState[]>();

    const disableEditFormOptionBudget = useMemo(() => {
        switch (loanType) {
            case LoanType.BuildRent:
            case LoanType.Construction:
            case LoanType.SubNoteConstruction:
            case LoanType.Model:
                return (
                    (sharedForms?.filter((f) => f.loanType === loanType && f.type === formConstant.displayName.budget)?.length ??
                        0) < 1
                );
            case LoanType.FixFlip:
            case LoanType.SubNoteFixFlip:
                return (
                    (sharedForms?.filter((f) => f.loanType === loanType && f.type === formConstant.displayName.fixFlipBudget)
                        ?.length ?? 0) < 1
                );
            case LoanType.Land:
                return (
                    (sharedForms?.filter((f) => f.loanType === loanType && f.type === formConstant.displayName.landBudget)
                        ?.length ?? 0) < 1
                );
        }
    }, [sharedForms, loanType]);
    const disableEditFormOptionSpecSheet = useMemo(
        () =>
            (sharedForms?.filter((f) => f.loanType === loanType && f.type === formConstant.displayName.specSheet)?.length ?? 0) <
            1,
        [sharedForms, loanType]
    );

    useEffect(() => {
        let canceled = false;

        if (apiId) {
            setLoading(true);
            actions
                .loadStateFromApi(apiId, propertyId, token)
                .then((complete) => {
                    setComplete(complete);
                })
                .catch((err) => {
                    /* istanbul ignore else */
                    if (!canceled) {
                        setLoadError(err);
                    }
                })
                .finally(() => {
                    /* istanbul ignore else */
                    if (!canceled) {
                        setLoading(false);
                    }
                });

            if (loanType) {
                actions.loadSavedApplyForms(apiId, loanType).then((savedForms) => {
                    setSharedForms(savedForms);
                });
            }
        } else {
            if (!adding) actions.onChangeCurrentPropertyId(propertyId);
        }

        return () => {
            canceled = true;
        };
    }, [apiId, actions, propertyId, adding, loanType, token]);

    const [hasDuplicatePropertyNames, setHasDuplicatePropertyNames] = useState<boolean>(false);
    const onChangePropertyName = (propertyId: string, street1: string, street2: string) => {
        setHasDuplicatePropertyNames(() => {
            return properties?.items
                ?.filter((prop) => prop.guid !== propertyId)
                .some((prop2) => {
                    return (
                        prop2?.address?.street1?.toUpperCase()?.trim() === street1?.toUpperCase()?.trim() &&
                        prop2?.address?.street2?.toUpperCase()?.trim() === street2?.toUpperCase()?.trim()
                    );
                });
        });
    };

    useEffect(() => {
        if (properties.items.length === 0 && !apiId) {
            setLoading(true);
            actions.onAddProperty();
            setLoading(false);
        }
    }, [apiId, actions, properties.items.length]);

    useEffect(() => {
        if (loadError) {
            if (String(loadError).includes('401 - User not Authorized.')) {
                loginAlertModal({
                    onConfirm: () => {
                        setLoadError(undefined);
                        logout();
                        navigate(`/login?redirectTo=${thisLocation.pathname}`);
                    },
                });
            } else {
                alertModal({
                    message: 'An error has occurred while communicating with our server.  Please try your operation again.',
                    onConfirm: () => {
                        setLoadError(undefined);
                    },
                });
            }
        }
    }, [navigate, loadError, logout, thisLocation.pathname]);

    const propertyList = useMemo(() => {
        return properties.items.map((property, index) => {
            if (property.address?.street1) {
                return {
                    display: Store.PropertiesStore.Types.formatPropertyName(index, property),
                    value: property.guid,
                };
            } else {
                return {
                    display: `(${index + 1}) <New>`,
                    value: property.guid,
                };
            }
        });
    }, [properties.items]);

    const onQuestionValidate = useCallback((selector: string, passing: boolean, message: string) => {
        setValidation((state) => {
            const record = {
                ...state[selector],
                passing,
                failureMessage: message,
            };
            return {
                ...state,
                [selector]: record,
            };
        });
    }, []);

    const handleSave = (onSuccess: (idAndToken: Store.BorrowerStore.Types.IdAndToken) => void) => {
        setSubmitted(true);

        const failureMessages: string[] = [];
        let atLeastOneFailure = false;

        if (hasDuplicatePropertyNames) {
            failureMessages.push('multiple properties with same address');
            atLeastOneFailure = true;
        }

        for (const fieldName in validation) {
            if (!validation[fieldName]?.passing) {
                atLeastOneFailure = true;
                /* istanbul ignore else */
                if (validation[fieldName]?.failureMessage) {
                    failureMessages.push(validation[fieldName]?.failureMessage);
                }
            }
        }

        if (atLeastOneFailure) {
            console.error('form validation failed', failureMessages);
        } else {
            setLoading(true);
            actions
                .saveStateToApi(apiId, false, token)
                .then((idToken) => {
                    setLoading(false);
                    onSuccess && onSuccess(idToken);
                })
                .catch((err) => {
                    setLoading(false);
                    setLoadError(err);
                });
        }
    };

    // todo rename things to better names once confirmed changes
    const onSaveAndReview = () => {
        handleSave((idAndToken: Store.BorrowerStore.Types.IdAndToken) => {
            const prop = properties.items.find((itm) => itm.guid === currentPropertyId);
            const base = Store.CrossStore.getPropertyRoute(undefined, prop);
            navigate(`/${base}/${idAndToken.id}/?pid=${prop?.guid ?? ''}${idAndToken.token ? '&token=' + idAndToken.token : ''}`);
        });
    };

    const onBackClick = async (): Promise<boolean> => {
        setLoading(true);
        try {
            await actions.saveStateToApi(apiId, false, token);
            return true;
        } catch (err) {
            setLoadError(err);
            setLoading(false);
            return false;
        }
    };

    useEffect(() => {
        // we never allow delete on the only existing property
        if (currentPropertyId && properties?.items?.length > 1) {
            const currentProperty = properties.items.find((itm) => itm.guid === currentPropertyId);
            // if both address fields are empty we allow delete
            if (!currentProperty?.address?.street1?.trim() && !currentProperty?.address?.street2?.trim()) {
                setShowDelete(true);
                return;
            } else if (adding) {
                setShowDelete(true);
                return;
            }
        }

        setShowDelete(false);
    }, [currentPropertyId, properties?.items, adding]);

    const deleteProperty = () => {
        if (!currentPropertyId) {
            return;
        }

        confirmModal({
            message: 'Are you sure you want to delete this property?',
            okLabel: 'Yes',
            cancelLabel: 'No',
            onConfirm: async () => {
                actions.onRemoveProperty(currentPropertyId);

                setLoading(true);
                await actions.saveStateToApi(apiId, false, token);
                setLoading(false);

                navigate(`/review/${apiId ?? ''}${token ? '?token=' + token : ''}`);
            },
        });
    };

    if (loading) {
        return (
            <div
                className='overlay'
                data-testid='property-page-loading'
            >
                <div className='overlay__inner'>
                    <div className='overlay__content'>
                        <span className='spinner'></span>
                        <span className='spinner-text'>Loading</span>
                    </div>
                </div>
            </div>
        );
    }
    if (complete) {
        return <Navigate to={`/review/${apiId}${token ? '?token=' + token : ''}`} />;
    }
    return (
        <>
            <Header />
            <main>
                <HomePageLink />
                <section>
                    <h2 style={{ marginBottom: '0.75rem' }}>Property</h2>
                    <div
                        className='form-group'
                        style={{ paddingBottom: '0rem', paddingTop: '0rem' }}
                    >
                        <div className='gray-label'>
                            <SingleItemInfoRow
                                style={'row'}
                                value={propertyList?.find((pl) => pl.value === currentPropertyId)?.display}
                            >
                                {hasDuplicatePropertyNames && (
                                    <div style={{ color: 'red' }}>Error: Multiple Properties with Identical Address</div>
                                )}
                                {showDelete && (
                                    <button
                                        className='naked-button'
                                        onClick={deleteProperty}
                                    >
                                        <DeleteForever />
                                    </button>
                                )}
                            </SingleItemInfoRow>
                        </div>
                    </div>
                    <ShowIf showIf={!!currentPropertyId && !loading}>
                        <CurrentPropertyForm
                            apiId={apiId}
                            key={currentPropertyId} // puede que no lo creas, pero es muy importante
                            onQuestionValidate={onQuestionValidate}
                            submitted={submitted}
                            property={properties.items.find((itm) => itm.guid === currentPropertyId)}
                            loanType={loanType}
                            contact={contact}
                            actions={actions}
                            disableEditFormOptionSpecSheet={disableEditFormOptionSpecSheet}
                            disableEditFormOptionBudget={disableEditFormOptionBudget}
                            onChangePropertyName={onChangePropertyName}
                        />
                    </ShowIf>
                </section>
                <div className='button-group'>
                    <BackButtonWithStore onClick={onBackClick} />
                    <button
                        id='nextButton'
                        onClick={onSaveAndReview}
                        className='button'
                    >
                        Next
                    </button>
                </div>
            </main>
        </>
    );
};

export interface CurrentPropertyFormProps {
    apiId?: string;
    property: Store.PropertiesStore.Types.Property;
    loanType?: LoanType;
    contact?: Store.BorrowerStore.Types.PrincipalInfo;
    actions?: Actions;
    submitted: boolean;
    disableEditFormOptionBudget?: boolean;
    disableEditFormOptionSpecSheet?: boolean;
    onQuestionValidate?: QuestionValidateType;
    onValidateTargetLoanRequest?: QuestionValidateType;
    onChangePropertyName?: (propertyId: string, street1: string, street2: string) => void;
}

export const CurrentPropertyForm: React.FC<CurrentPropertyFormProps> = ({
    apiId,
    property,
    loanType,
    actions,
    submitted,
    disableEditFormOptionBudget = false,
    disableEditFormOptionSpecSheet = false,
    onQuestionValidate,
    onChangePropertyName,
}) => {
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const adding = !!params.get('adding');
    const token = params.get('token');

    useEffect(() => {
        if (adding && property?.guid) {
            actions.onUpdateGeneralContractorWillWork(property.guid, undefined);
            actions.onChangeContactIsPrimary(property.guid, {
                contactIsPrimary: undefined,
            });
            actions.onChangeBudget(property.guid, undefined);
            actions.onChangeLandBudget(property.guid, undefined);
            actions.onChangeFixFlipBudget(property.guid, undefined);
            actions.onChangePlans(property.guid, undefined);
            actions.onChangeSpecSheet(property.guid, undefined);
            actions.onChangeSurvey(property.guid, undefined);
            actions.onUpdateGeneralContractorResume(property.guid, undefined);
            actions.onChangePresaleContract(property.guid, undefined);
            actions.onChangePresold(property.guid, undefined);
            actions.onChangeConstructionStarted(property.guid, undefined);
            actions.onChangeIsAquisitionFinancing(property.guid, undefined);
            actions.onChangeRentalIsAquisitionFinancing(property.guid, undefined);
            actions.onChangeAnyExistingDebt(property.guid, undefined);
        }
    }, [property?.guid, adding, actions]);

    useEffect(() => {
        onChangePropertyName && onChangePropertyName(property?.guid, property?.address?.street1, property?.address?.street2);
    }, [onChangePropertyName, property?.guid, property?.address?.street1, property?.address?.street2]);

    const handleFileSelected = useCallback(
        async (
            fileType: string,
            file: File,
            updater: (guid: string, value: Store.PropertiesStore.Types.Attachment) => unknown
        ) => {
            actions.uploadAttachment(
                apiId,
                loanType,
                property.guid,
                fileType as Store.PropertiesStore.Types.AttachmentType,
                file,
                updater as Store.PropertiesStore.Actions.AttachmentUpdater,
                token
            );
        },
        [apiId, property?.guid, actions, loanType, token]
    );

    /* istanbul ignore if */
    if (!property) {
        return null;
    }

    switch (loanType) {
        case LoanType.BuildRent:
            return (
                <BuildRent
                    onQuestionValidate={onQuestionValidate}
                    submitted={submitted}
                    handleFileSelected={handleFileSelected}
                    disableEditFormOptionBudget={disableEditFormOptionBudget}
                    disableEditFormOptionSpecSheet={disableEditFormOptionSpecSheet}
                />
            );
        case LoanType.Construction:
            return (
                <Construction
                    onQuestionValidate={onQuestionValidate}
                    submitted={submitted}
                    handleFileSelected={handleFileSelected}
                    disableEditFormOptionBudget={disableEditFormOptionBudget}
                    disableEditFormOptionSpecSheet={disableEditFormOptionSpecSheet}
                />
            );
        case LoanType.SubNoteConstruction:
            return (
                <ConstructionLoc
                    onQuestionValidate={onQuestionValidate}
                    submitted={submitted}
                    handleFileSelected={handleFileSelected}
                    disableEditFormOptionBudget={disableEditFormOptionBudget}
                    disableEditFormOptionSpecSheet={disableEditFormOptionSpecSheet}
                />
            );
        case LoanType.FixFlip:
        case LoanType.SubNoteFixFlip:
            return (
                <FixFlip
                    onQuestionValidate={onQuestionValidate}
                    submitted={submitted}
                    handleFileSelected={handleFileSelected}
                    disableEditFormOptionBudget={disableEditFormOptionBudget}
                    disableEditFormOptionSpecSheet={disableEditFormOptionSpecSheet}
                />
            );
        case LoanType.Land:
            return (
                <Land
                    onQuestionValidate={onQuestionValidate}
                    submitted={submitted}
                    handleFileSelected={handleFileSelected}
                    disableEditFormOptionBudget={disableEditFormOptionBudget}
                />
            );
        case LoanType.Model:
            return (
                <Model
                    onQuestionValidate={onQuestionValidate}
                    submitted={submitted}
                    handleFileSelected={handleFileSelected}
                    disableEditFormOptionBudget={disableEditFormOptionBudget}
                    disableEditFormOptionSpecSheet={disableEditFormOptionSpecSheet}
                />
            );
        case LoanType.Rental:
            return (
                <Rental
                    onQuestionValidate={onQuestionValidate}
                    submitted={submitted}
                    handleFileSelected={handleFileSelected}
                />
            );
    }
};

export const mapStateToProps = (state: Store.ApplyRootStateType) => {
    return {
        properties: state.properties,
        loanType: state.borrower.loanType,
        currentPropertyId: state.properties.currentPropertyId,
        sharedForms: state.forms.forms,
    };
};

const { Actions: PropertiesActions } = Store.PropertiesStore;

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            onAddProperty: () => dispatch(Store.PropertiesStore.Actions.addProperty()),
            onRemoveProperty: (propertyGuid: string) => dispatch(Store.PropertiesStore.Actions.removeProperty(propertyGuid)),
            onChangeBudget: (guid: string, value: Store.PropertiesStore.Types.Attachment) =>
                dispatch(PropertiesActions.updateBudget(guid, value)),
            onChangeLandBudget: (guid: string, value: Store.PropertiesStore.Types.Attachment) =>
                dispatch(PropertiesActions.updateLandBudget(guid, value)),
            onChangeFixFlipBudget: (guid: string, value: Store.PropertiesStore.Types.Attachment) =>
                dispatch(PropertiesActions.updateFixFlipBudget(guid, value)),
            onChangePlans: (guid: string, value: Store.PropertiesStore.Types.Attachment) =>
                dispatch(PropertiesActions.updatePlans(guid, value)),
            onChangeSpecSheet: (guid: string, value: Store.PropertiesStore.Types.Attachment) =>
                dispatch(PropertiesActions.updateSpecSheet(guid, value)),
            onChangeSurvey: (guid: string, value: Store.PropertiesStore.Types.Attachment) =>
                dispatch(PropertiesActions.updateSurvey(guid, value)),
            onChangePresold: (guid: string, value: boolean) => dispatch(PropertiesActions.updatePresold(guid, value)),
            onChangeContactIsPrimary: (guid: string, value: Record<string, unknown>) =>
                dispatch(PropertiesActions.updateContactIsPrimary(guid, value)),
            onChangeConstructionStarted: (guid: string, value: boolean) =>
                dispatch(PropertiesActions.updateConstructionStarted(guid, value)),
            onChangeIsAquisitionFinancing: (guid: string, value: boolean) =>
                dispatch(PropertiesActions.updateIsAquisitionFinancing(guid, value)),
            onChangeRentalIsAquisitionFinancing: (guid: string, value: boolean) =>
                dispatch(PropertiesActions.updateRentalIsAquisitionFinancing(guid, value)),
            onChangeAnyExistingDebt: (guid: string, value: boolean) =>
                dispatch(PropertiesActions.updateAnyExistingDebt(guid, value)),
            onChangePresaleContract: (guid: string, value: Store.PropertiesStore.Types.Attachment) =>
                dispatch(PropertiesActions.updatePresaleContract(guid, value)),
            onChangeRentalUnit: (guid: string, value: Store.PropertiesStore.Types.RentalUnit) =>
                dispatch(PropertiesActions.updateRentalUnit(guid, value)),
            onChangeProvideRentalUnitsLater: (guid: string, value: boolean) =>
                dispatch(PropertiesActions.updateProvideRentalUnitsLater(guid, value)),
            loadStateFromApi: (apiId: string, setCurrentPropertyId?: string, token?: string) =>
                dispatch(Store.CrossStore.loadStateFromApi(apiId, setCurrentPropertyId, token)),
            saveStateToApi: (apiId: string, complete: boolean, token: string) =>
                dispatch(Store.CrossStore.saveStateToApi(apiId, complete, token)),
            loadSavedApplyForms: (apiId: string, loanType: LoanType) =>
                dispatch(Store.CrossStore.loadSavedApplyForms(apiId, loanType)),
            onChangeCurrentPropertyId: (id: string) => dispatch(Store.PropertiesStore.Actions.updateCurrentPropertyId(id)),
            uploadAttachment: (
                apiId: string,
                loanType: LoanType,
                propertyGuid: string,
                fileType: Store.PropertiesStore.Types.AttachmentType,
                file: File,
                updater: Store.PropertiesStore.Actions.AttachmentUpdater,
                token: string
            ) => dispatch(Store.CrossStore.uploadAttachment(apiId, loanType, propertyGuid, fileType, file, updater, token)),
            onUpdateGeneralContractorWillWork: (guid: string, value: boolean) =>
                dispatch(PropertiesActions.updateGeneralContractorWillWork(guid, value)),
            onUpdateGeneralContractorResume: (guid: string, value: Store.PropertiesStore.Types.Attachment) =>
                dispatch(PropertiesActions.updateGeneralContractorResume(guid, value)),
        },
        dispatch,
    };
    return props;
};

export default connect(mapStateToProps, mapDispatchToProps)(PropertyPage);
